import { getLastTranslationProvider, setTranslationProvider, translatePosts } from './translation'
import { articleTextFromHtml } from '../utils/sanitizeHtml'

const BLUE_TRACKER_RSS_URL = 'https://www.wowhead.com/blue-tracker?rss'
const WOWHUB_NEWS_URL = 'https://wowhub.com.ua/news/'
const WOWHUB_ORIGIN = 'https://wowhub.com.ua'
const WOWHUB_ARTICLE_PATH_PATTERN = /^\/\d{4}\/\d{2}\/[^/]+\/?$/
const WOWHEAD_NEWS_FEEDS = [
  {
    key: 'all',
    label: 'All News',
    shortLabel: 'All',
    url: 'https://www.wowhead.com/news/rss/retail',
    kind: 'aggregate',
    sources: ['retail', 'classic', 'blue'],
  },
  {
    key: 'blue',
    label: 'Blue Tracker',
    shortLabel: 'Blue',
    url: BLUE_TRACKER_RSS_URL,
    kind: 'blue',
  },
  {
    key: 'retail',
    label: 'Retail WoW',
    shortLabel: 'Retail',
    url: 'https://www.wowhead.com/news/rss/retail',
    kind: 'news',
  },
  {
    key: 'classic',
    label: 'Classic WoW',
    shortLabel: 'Classic',
    url: 'https://www.wowhead.com/news/rss/classic',
    kind: 'news',
  },
  {
    key: 'wowhub',
    label: 'WoW Hub',
    shortLabel: 'WoW Hub',
    url: WOWHUB_NEWS_URL,
    kind: 'wowhub',
    source: 'wowhub',
    sourceLabel: 'WoW Hub',
    origin: WOWHUB_ORIGIN,
    nativeLanguage: 'uk',
  },
]
const WOWHEAD_WORKER_URL = 'https://wowhead-rss-proxy.andrey-ilutsa.workers.dev'
const WOWHEAD_ARTICLE_PARSER_VERSION = '2026-08-11-wowhub-wordpress-v6-dungeon-cards'
const FEED_CACHE_TTL_MS = 5 * 60 * 1000
const FEED_STORAGE_PREFIX = 'rio-news-feed-cache-v3:'
const feedCache = new Map()
const feedRequests = new Map()
const feedRequestVersions = new Map()
const feedFallbacks = new Map()

function createWorkerUrl(path, params = {}) {
  const workerUrl = new URL(path, WOWHEAD_WORKER_URL)
  Object.entries(params).forEach(([key, value]) => {
    if (value) workerUrl.searchParams.set(key, value)
  })
  return workerUrl.toString()
}

function createProxyUrl(url) {
  return createWorkerUrl('/', { url })
}

function decodeHtml(value = '') {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = value
  return textarea.value
}

function stripHtml(value = '') {
  // Parsed via DOMParser rather than a live element: article HTML is untrusted and
  // `<img onerror>` fires even in a detached div.
  return articleTextFromHtml(value)
}

function cleanFeedSummary(value = '') {
  return String(value || '')
    .replace(/\s*Continue reading\s*»?\s*$/i, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function normalizedPostTextKey(value = '') {
  return cleanFeedSummary(value)
    .replace(/\s+By[A-Za-z0-9 ._-]+ on \d{4}\/\d{2}\/\d{2} at \d{1,2}:\d{2}\s*(?:AM|PM)?\s*$/i, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

const ENCODED_ARTICLE_TAG_PATTERN = /&lt;\/?(?:a|b|blockquote|br|code|div|em|h[1-6]|i|li|ol|p|span|strong|table|tbody|td|th|thead|tr|ul)\b/i

function cleanFeedSummaryText(value = '') {
  return String(value || '')
    .replace(/\s*Continue reading\s*(?:\u00bb|\u0412\u00bb|&raquo;)?\s*$/i, '')
    .replace(/\s*(?:\.{3}|\u2026)?\s*By\s*.+?\s+on\s+\d{4}\/\d{2}\/\d{2}\s+at\s+\d{1,2}:\d{2}\s*(?:AM|PM)?\s*$/i, '')
    .replace(/\[\/?(?:b|i|u|s|center|left|right|quote|spoiler|fancy)(?:=[^\]]*)?\]/gi, ' ')
    .replace(/\[url=[^\]]+\]([\s\S]*?)\[\/url\]/gi, '$1')
    .replace(/\[img\][\s\S]*?\[\/img\]/gi, ' ')
    .replace(/"?quotquot"?/gi, '"')
    .replace(/\s+/g, ' ')
    .trim()
}

function normalizedFeedTextKey(value = '') {
  return cleanFeedSummaryText(value)
    .replace(/\s*(?:\.{3}|\u2026)?\s*By\s*.+?\s+on\s+\d{4}\/\d{2}\/\d{2}\s+at\s+\d{1,2}:\d{2}\s*(?:AM|PM)?\s*$/i, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

function bluePostDedupeKeys(post) {
  if (post.kind !== 'blue') return []

  const titleKey = normalizedFeedTextKey(post.title)
    .replace(/\b(?:january|february|march|april|may|june|july|august|september|october|november|december)\b/g, '')
    .replace(/\b\d{1,4}\b/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  const summaryKey = normalizedFeedTextKey(post.summary)
  const keys = []

  if (titleKey.length > 18) keys.push(`blue-title:${titleKey}`)
  if (summaryKey.length > 40) keys.push(`blue-summary:${summaryKey}`)

  return keys
}

function decodeEscapedArticleHtml(value = '') {
  let html = String(value || '')

  for (let attempts = 0; attempts < 5 && ENCODED_ARTICLE_TAG_PATTERN.test(html); attempts += 1) {
    html = decodeHtml(html)
  }

  return html
}

function normalizeArticleBlock(block = {}) {
  if (!block || typeof block !== 'object') return block

  if (['html', 'quote', 'bluepost'].includes(block.type)) {
    return {
      ...block,
      html: decodeEscapedArticleHtml(block.html || block.text || ''),
    }
  }

  if (block.type === 'list') {
    return {
      ...block,
      items: (block.items || []).map((item) => decodeEscapedArticleHtml(item)),
    }
  }

  if (['paragraph', 'heading'].includes(block.type)) {
    const decoded = decodeEscapedArticleHtml(block.text || '')
    return {
      ...block,
      text: /<\/?[a-z][^>]*>/i.test(decoded) ? stripHtml(decoded).replace(/\s+/g, ' ').trim() : decoded,
    }
  }

  return block
}

function meaningfulArticleBlock(block = {}) {
  if (['image', 'video', 'embed'].includes(block.type)) return Boolean(block.src)
  if (block.type === 'list') return (block.items || []).some((item) => stripHtml(item).trim().length > 12)
  return stripHtml(block.html || block.text || '').trim().length > 12
}

function textFromNode(node, selector) {
  return decodeHtml(node.querySelector(selector)?.textContent?.trim() || '')
}

function attrFromNode(node, selector, attr) {
  return node.querySelector(selector)?.getAttribute(attr)?.trim() || ''
}

function normalizeLink(value = '', baseUrl = 'https://www.wowhead.com') {
  const link = decodeHtml(value).trim()
  if (!link) return ''
  try {
    const url = new URL(link, baseUrl)
    // WoW Hub article URLs are `/YYYY/MM/slug/`; the trailing slash and the stripped tracking
    // query keep feed links, article links and cache keys identical.
    if (/^(?:www\.)?wowhub\.com\.ua$/i.test(url.hostname)) {
      url.protocol = 'https:'
      url.hostname = 'wowhub.com.ua'
      url.search = ''
      url.hash = ''
      if (WOWHUB_ARTICLE_PATH_PATTERN.test(url.pathname)) url.pathname = url.pathname.replace(/\/?$/, '/')
    }
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : ''
  } catch {
    return ''
  }
}

function getCategory(item, feed) {
  const title = textFromNode(item, 'title')
  const titleCategory = title.match(/\[(.*?)\]/)?.[1]
  const category = item.querySelector('category')?.textContent?.trim()

  if (feed?.kind === 'blue') {
    const decodedCategory = decodeHtml(category || '')
    if (titleCategory) return decodeHtml(titleCategory)
    if (decodedCategory && !/^blue post$/i.test(decodedCategory)) return decodedCategory

    const signal = `${title} ${textFromNode(item, 'link')}`
    return /\b(?:classic|cataclysm|mists of pandaria|hardcore|season of discovery|burning crusade|wrath of the lich king)\b/i.test(signal)
      ? 'WoW Classic'
      : 'Retail WoW'
  }

  if (category) return decodeHtml(category)
  return titleCategory || 'Blue post'
}

function parseDate(value) {
  const timestamp = Date.parse(value || '')
  return Number.isNaN(timestamp) ? null : new Date(timestamp).toISOString()
}

function findImageUrl(item, rawDescription, origin) {
  const rawContent = textFromNode(item, 'content\\:encoded')
  const imagePattern = /https?:\/\/[^\s<>"')\]]+\.(?:avif|gif|jpe?g|png|webp)(?:\?[^\s<>"')\]]*)?/i
  const candidates = [
    attrFromNode(item, 'media\\:content', 'url'),
    attrFromNode(item, 'media\\:thumbnail', 'url'),
    attrFromNode(item, 'enclosure', 'url'),
    rawContent.match(/<img[^>]+src=["']([^"']+)["']/i)?.[1],
    rawDescription.match(/<img[^>]+src=["']([^"']+)["']/i)?.[1],
    rawContent.match(/\[img\]([^\[]+)\[\/img\]/i)?.[1],
    rawDescription.match(/\[img\]([^\[]+)\[\/img\]/i)?.[1],
    rawContent.match(imagePattern)?.[0],
    rawDescription.match(imagePattern)?.[0],
    textFromNode(item, 'image url'),
  ].filter(Boolean)

  const image = candidates
    .map((candidate) => normalizeLink(candidate, origin))
    .find((candidate) => candidate && !/\/Themes\/[^/]+\/images\/(?:topic\/)?(?:normal_post|new|on|off)\.(?:gif|png)$/i.test(candidate))

  return image || ''
}

function parseFeed(xmlText, feed = WOWHEAD_NEWS_FEEDS[0]) {
  const documentXml = new DOMParser().parseFromString(xmlText, 'application/xml')
  const parserError = documentXml.querySelector('parsererror')

  if (parserError) {
    throw new Error('Unable to parse Wowhead RSS feed.')
  }

  return [...documentXml.querySelectorAll('item')].map((item, index) => {
    const rawDescription = textFromNode(item, 'description')
    const summary = cleanFeedSummaryText(stripHtml(rawDescription))
    const title = textFromNode(item, 'title').replace(/^\[[^\]]+\]\s*/, '')

    return {
      id: textFromNode(item, 'guid') || normalizeLink(textFromNode(item, 'link'), feed.origin) || `${feed.key}-post-${index}`,
      title,
      summary,
      category: getCategory(item, feed),
      feedKey: feed.key,
      feedLabel: feed.label,
      kind: feed.kind,
      source: feed.source || 'wowhead',
      sourceLabel: feed.sourceLabel || 'Wowhead',
      nativeLanguage: feed.nativeLanguage || 'en',
      imageUrl: findImageUrl(item, rawDescription, feed.origin),
      link: normalizeLink(textFromNode(item, 'link'), feed.origin),
      publishedAt: parseDate(textFromNode(item, 'pubDate')),
    }
  })
}

function uniquePosts(posts) {
  const seen = new Set()
  return posts.filter((post) => {
    const contentKey = [
      normalizedFeedTextKey(post.title),
      normalizedFeedTextKey(post.summary),
    ].filter(Boolean).join('|')
    const keys = [
      post.link,
      post.id,
      contentKey ? `content:${contentKey}` : '',
      ...bluePostDedupeKeys(post),
    ].filter(Boolean)

    if (keys.some((key) => seen.has(key))) return false
    keys.forEach((key) => seen.add(key))
    return true
  })
}

function postTimestamp(post) {
  const timestamp = Date.parse(post?.publishedAt || '')
  return Number.isNaN(timestamp) ? 0 : timestamp
}

// Undated posts sort last. Subtracting two NaNs would make the comparator inconsistent,
// which leaves the entries around them in an arbitrary order rather than chronological.
function sortPostsByDate(posts) {
  return [...posts].sort((left, right) => postTimestamp(right) - postTimestamp(left))
}

async function requestFeed(url) {
  const response = await fetch(url, {
    headers: {
      Accept: 'application/rss+xml, application/xml, text/xml;q=0.9, */*;q=0.8',
    },
  })

  if (!response.ok) {
    throw new Error(`Wowhead RSS request failed: ${response.status}`)
  }

  return response.text()
}

async function requestJson(url) {
  const response = await fetch(url, {
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Wowhead worker request failed: ${response.status}`)
  }

  return response.json()
}

export async function getBlueTrackerPosts(lang = 'en') {
  return getWowheadFeedPosts('blue', lang)
}

function getFeedCacheEntry(feedKey = 'blue', lang = 'en') {
  const requestKey = `${feedKey}:${lang}`
  const cached = feedCache.get(requestKey)
  if (cached && Date.now() - cached.timestamp < FEED_CACHE_TTL_MS) return cached
  if (lang !== 'en') return null

  const persisted = loadPersistedFeedCache(feedKey)
  if (!persisted || Date.now() - persisted.timestamp >= FEED_CACHE_TTL_MS) return null

  const hydrated = {
    posts: persisted.posts,
    timestamp: persisted.timestamp,
    translationProvider: null,
  }
  feedCache.set(requestKey, hydrated)
  return hydrated
}

function updateFeedRequestContext(options, values) {
  if (!options.cacheContext) return
  Object.assign(options.cacheContext, values)
}

function rememberFeedCache(feedKey, lang, posts) {
  const requestKey = `${feedKey}:${lang}`
  feedCache.set(requestKey, {
    posts,
    timestamp: Date.now(),
    translationProvider: lang === 'en' ? null : getLastTranslationProvider(),
  })

  if (lang === 'en') persistFeedCache(feedKey, posts)
}

function persistFeedCache(feedKey, posts) {
  try {
    localStorage.setItem(`${FEED_STORAGE_PREFIX}${feedKey}`, JSON.stringify({
      timestamp: Date.now(),
      posts,
    }))
  } catch {
    // The in-memory cache remains available when storage is full or disabled.
  }
}

function loadPersistedFeedCache(feedKey) {
  try {
    const cached = JSON.parse(localStorage.getItem(`${FEED_STORAGE_PREFIX}${feedKey}`) || 'null')
    return Array.isArray(cached?.posts) && cached.posts.length
      ? { posts: cached.posts, timestamp: Number(cached.timestamp || 0) }
      : null
  } catch {
    return null
  }
}

export function getFeedFallbackStatus(feedKey = 'blue') {
  return feedFallbacks.get(feedKey) || null
}

function clearFeedCache(feedKey = 'blue', lang = '') {
  [...feedCache.keys()].forEach((key) => {
    if (key === `${feedKey}:${lang}` || (!lang && key.startsWith(`${feedKey}:`))) {
      feedCache.delete(key)
    }
  })
}

export async function getWowheadFeedPosts(feedKey = 'blue', lang = 'en', options = {}) {
  if (options.force) clearFeedCache(feedKey, lang)

  const requestKey = `${feedKey}:${lang}`
  const cached = options.force ? null : getFeedCacheEntry(feedKey, lang)

  if (cached) {
    updateFeedRequestContext(options, {
      source: 'cache',
      updatedAt: cached.timestamp,
    })
    if (lang !== 'en' && cached.translationProvider) {
      setTranslationProvider({
        ...cached.translationProvider,
        cached: true,
      })
    }
    return cached.posts
  }

  if (!options.force && feedRequests.has(requestKey)) {
    updateFeedRequestContext(options, { source: 'network' })
    return feedRequests.get(requestKey)
  }

  updateFeedRequestContext(options, { source: 'network' })
  const requestVersion = (feedRequestVersions.get(requestKey) || 0) + 1
  feedRequestVersions.set(requestKey, requestVersion)

  const request = loadWowheadFeedPosts(feedKey, lang)
    .then((posts) => {
      updateFeedRequestContext(options, { updatedAt: Date.now() })
      if (feedRequestVersions.get(requestKey) === requestVersion) {
        feedFallbacks.delete(feedKey)
        rememberFeedCache(feedKey, lang, posts)
      }
      return posts
    })
    .catch((error) => {
      const persisted = lang === 'en' ? loadPersistedFeedCache(feedKey) : null
      if (!persisted) throw error

      updateFeedRequestContext(options, {
        source: 'fallback',
        updatedAt: persisted.timestamp,
      })
      if (feedRequestVersions.get(requestKey) === requestVersion) {
        feedFallbacks.set(feedKey, {
          timestamp: persisted.timestamp,
          reason: error?.message || 'Feed request failed',
        })
        feedCache.set(requestKey, {
          posts: persisted.posts,
          timestamp: Date.now(),
          translationProvider: null,
        })
      }
      return persisted.posts
    })
    .finally(() => {
      if (feedRequestVersions.get(requestKey) === requestVersion) {
        feedRequests.delete(requestKey)
      }
    })

  feedRequests.set(requestKey, request)
  return request
}

export async function getWowheadFeedPostsRaw(feedKey = 'blue', options = {}) {
  return getWowheadFeedPosts(feedKey, 'en', options)
}

export async function getTranslatedWowheadFeedPosts(feedKey = 'blue', lang = 'en', options = {}) {
  if (lang === 'en') return getWowheadFeedPostsRaw(feedKey, options)
  if (options.force) clearFeedCache(feedKey, lang)

  const cached = getFeedCacheEntry(feedKey, lang)
  if (cached) {
    if (cached.translationProvider) {
      setTranslationProvider({
        ...cached.translationProvider,
        cached: true,
      })
    }
    return {
      posts: cached.posts,
      fromCache: true,
      translationProvider: cached.translationProvider || null,
    }
  }

  const sourcePosts = Array.isArray(options.sourcePosts)
    ? options.sourcePosts
    : await getWowheadFeedPostsRaw(feedKey, options)
  const posts = await translatePosts(sortPostsByDate(uniquePosts(sourcePosts)), lang)
  rememberFeedCache(feedKey, lang, posts)
  return {
    posts,
    fromCache: false,
    translationProvider: getLastTranslationProvider(),
  }
}

async function loadWowheadFeedPosts(feedKey = 'blue', lang = 'en') {
  const posts = await loadWowheadFeedPostsRaw(feedKey)
  return lang === 'en' ? posts : translatePosts(posts, lang)
}

async function loadWowheadFeedPostsRaw(feedKey = 'blue') {
  const feed = WOWHEAD_NEWS_FEEDS.find((item) => item.key === feedKey) || WOWHEAD_NEWS_FEEDS[0]

  if (feed.source === 'wowhub') {
    const payload = await requestJson(createWorkerUrl('/wowhub-feed', {
      parser: WOWHEAD_ARTICLE_PARSER_VERSION,
    }))
    const posts = Array.isArray(payload.posts) ? payload.posts : []

    if (!posts.length) throw new Error('WoW Hub did not return news posts.')

    return sortPostsByDate(uniquePosts(posts.map((post, index) => ({
      id: normalizeLink(post.id || post.link, feed.origin) || `wowhub-post-${index}`,
      title: decodeHtml(post.title || ''),
      summary: cleanFeedSummaryText(post.summary || ''),
      category: post.category || feed.label,
      feedKey: feed.key,
      feedLabel: feed.label,
      kind: feed.kind,
      source: feed.source,
      sourceLabel: feed.sourceLabel,
      nativeLanguage: feed.nativeLanguage,
      imageUrl: normalizeLink(post.imageUrl || '', feed.origin),
      link: normalizeLink(post.link || '', feed.origin),
      publishedAt: parseDate(post.publishedAt),
      author: post.author || '',
    }))))
  }

  if (feed.kind === 'aggregate') {
    const sourceFeeds = feed.sources
      .map((key) => WOWHEAD_NEWS_FEEDS.find((item) => item.key === key))
      .filter(Boolean)
    const results = await Promise.all(sourceFeeds.map(async (sourceFeed) => parseFeed(await requestFeed(createProxyUrl(sourceFeed.url)), sourceFeed)))
    return sortPostsByDate(uniquePosts(results.flat()))
  }

  return sortPostsByDate(uniquePosts(parseFeed(await requestFeed(createProxyUrl(feed.url)), feed)))
}

export async function getWowheadArticleImage(url, source = 'wowhead') {
  const article = await getNewsArticle(url, source)
  return article.imageUrl || ''
}

export async function getNewsArticle(url, source = 'wowhead') {
  const isWowhub = source === 'wowhub'
  const normalizedUrl = normalizeLink(url, isWowhub ? WOWHUB_ORIGIN : 'https://www.wowhead.com')
  const article = await requestJson(createWorkerUrl('/article', {
    url: normalizedUrl,
    source,
    parser: WOWHEAD_ARTICLE_PARSER_VERSION,
  }))
  const articleTitle = article.title || (isWowhub ? 'WoW Hub article' : 'Wowhead article')
  const sourceBlocks = Array.isArray(article.blocks) ? article.blocks : []
  const emptyBlockText = isWowhub
    ? 'Повний текст тимчасово недоступний. Відкрийте оригінал статті на WoW Hub.'
    : 'Open the source article on Wowhead to read the full post.'
  const parsedBlocks = sourceBlocks.length
    ? sourceBlocks.map(normalizeArticleBlock)
    : [{ type: 'paragraph', text: article.description || emptyBlockText }]
  const blocks = isWowhub && !parsedBlocks.some(meaningfulArticleBlock)
    ? [{ type: 'paragraph', text: article.description || emptyBlockText }]
    : parsedBlocks
  const fallbackImageUrl = blocks.find((block) => block.type === 'image' && block.src)?.src || ''

  return {
    title: articleTitle,
    description: article.description || '',
    imageUrl: article.imageUrl || fallbackImageUrl,
    author: article.author || '',
    publishedAt: article.publishedAt || '',
    url: article.url || normalizedUrl,
    schemaVersion: Number(article.schemaVersion || 1),
    contentStatus: article.contentStatus || (blocks.some(meaningfulArticleBlock) ? 'full' : 'fallback'),
    blocks,
  }
}

export const getWowheadArticle = getNewsArticle

export { BLUE_TRACKER_RSS_URL, WOWHUB_NEWS_URL, WOWHEAD_NEWS_FEEDS }
